import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getQuotaUsage, type QuotaUsage } from '../services/quotaService';
import { useAuthContext } from './AuthContext';
import { Logger } from '../utils/logger';

interface QuotaContextType {
  percentageUsed: number;
  isQuotaExceeded: boolean;
  isLoading: boolean;
  quota: QuotaUsage | null;
  creditsUsed: number;
  creditsLimit: number;
  purchasedCreditsBalance: number;
  canPurchaseTopup: boolean;
  hasWatermark: boolean;
  refreshQuota: () => Promise<void>;
}

const QuotaContext = createContext<QuotaContextType | null>(null);

const QUOTA_POLL_INTERVAL = 60_000;
const QUOTA_BACKOFF_MAX = 300_000; // 5 min cap

export function QuotaProvider({ children }: { children: React.ReactNode }) {
  // V4-QUOTA-POLL-UNAUTHENTICATED (2026-07-03) — le provider est monté globalement
  // (App.tsx, hors garde d'auth), donc sans ce check il continue de poller
  // /quota/usage sur les pages publiques (login, signup) : 401 en boucle, chacun
  // déclenchant un cycle refresh+logout inutile côté apiClient (bruit console +
  // requêtes gaspillées, observé pendant le smoke E2E du 2026-07-03).
  const { user } = useAuthContext();
  const [quota, setQuota] = useState<QuotaUsage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const failCountRef = useRef(0);
  // V4-SETTINGS-LOCKEDSECTION-TS-LINT — ref-indirection pour casser la récursion mutuelle
  // scheduleNext↔fetchQuota sans no-use-before-define (ni warning exhaustive-deps). scheduleNext
  // appelle la dernière version de fetchQuota via la ref, assignée juste après sa définition.
  const fetchQuotaRef = useRef<() => void>(() => {});

  const scheduleNext = useCallback((delay: number) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => fetchQuotaRef.current(), delay);
  }, []);

  const fetchQuota = useCallback(async () => {
    try {
      const data = await getQuotaUsage();
      setQuota(data);
      failCountRef.current = 0;
      scheduleNext(QUOTA_POLL_INTERVAL);
    } catch (e) {
      failCountRef.current += 1;
      // Exponential backoff: 60s, 120s, 240s … capped at 5 min
      const backoff = Math.min(QUOTA_POLL_INTERVAL * 2 ** (failCountRef.current - 1), QUOTA_BACKOFF_MAX);
      Logger.error(`[QuotaContext] Failed to fetch quota (retry in ${backoff / 1000}s)`, e);
      scheduleNext(backoff);
    } finally {
      setIsLoading(false);
    }
  }, [scheduleNext]);
  // Garde la ref à jour vers la dernière version de fetchQuota (cf. fetchQuotaRef plus haut).
  fetchQuotaRef.current = fetchQuota;

  // Fetch on mount, backoff-based scheduling takes over afterwards
  // Pause polling when tab is hidden, resume and refresh immediately when visible again
  // Ne poller que si un utilisateur est authentifié (cf. V4-QUOTA-POLL-UNAUTHENTICATED
  // ci-dessus) — sinon /quota/usage échoue en boucle sur les pages publiques.
  useEffect(() => {
    if (!user) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      setIsLoading(false);
      return;
    }

    fetchQuota();

    // TODO: Ajouter un flag `isFetching` (useRef<boolean>) et un debounce de 500ms
    // sur le handleVisibilityChange pour éviter les requêtes concurrentes si l'utilisateur
    // switch rapidement entre onglets. Pattern : if (isFetchingRef.current) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        fetchQuota();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchQuota, user]);

  const refreshQuota = useCallback(async () => {
    await fetchQuota();
  }, [fetchQuota]);

  const isQuotaExceeded = Boolean(
    quota && quota.quota_exceeded && quota.enforcement_enabled
  );

  const creditsUsed = quota?.credits_used ?? 0;
  const creditsLimit = quota?.credits_limit ?? 0;
  const purchasedCreditsBalance = quota?.purchased_credits_balance ?? 0;
  const canPurchaseTopup = quota?.can_purchase_topup ?? false;
  const hasWatermark = quota?.has_watermark ?? true;

  const value = useMemo<QuotaContextType>(() => ({
    percentageUsed: quota?.percentage_used ?? 0,
    isQuotaExceeded,
    isLoading,
    quota,
    creditsUsed,
    creditsLimit,
    purchasedCreditsBalance,
    canPurchaseTopup,
    hasWatermark,
    refreshQuota,
  }), [
    quota,
    isQuotaExceeded,
    isLoading,
    creditsUsed,
    creditsLimit,
    purchasedCreditsBalance,
    canPurchaseTopup,
    hasWatermark,
    refreshQuota,
  ]);

  return (
    <QuotaContext.Provider value={value}>
      {children}
    </QuotaContext.Provider>
  );
}

export function useQuota(): QuotaContextType {
  const ctx = useContext(QuotaContext);
  if (!ctx) {
    throw new Error('useQuota must be used within a QuotaProvider');
  }
  return ctx;
}
